import { Button, Input } from 'components/atoms';
import { useVideoPlayer } from 'hooks/useVideoPlayer';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCornersOut,
  PiPauseFill,
  PiPlayFill,
  PiSpeakerHigh,
  PiSpeakerX,
} from 'react-icons/pi';
import { useGetVideo, usePostVideoPlaybackTicket } from 'services/video';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import './VideoPlayer.scss';

interface IVideoPlayerProps {
  videoId?: string;
  src?: string | null;
}

const formatPlaybackTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return '0:00';
  }

  const totalSeconds = Math.floor(seconds);
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const VideoPlayer = ({ videoId, src }: IVideoPlayerProps) => {
  const { t } = useTranslation();
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);

  const video = useGetVideo(videoId ?? '', !!videoId && !src);
  const videoPlaybackTicket = usePostVideoPlaybackTicket();

  const playerMeta = useMemo(
    () =>
      video.data
        ? {
            title: video.data.mainTitle.title,
            artists: video.data.artists,
          }
        : undefined,
    [video.data]
  );

  const {
    videoRef,
    isPlaying,
    currentTime,
    duration,
    aspectRatio,
    volume,
    handleLoadedMetadata,
    handleTimeUpdate,
    togglePlay,
    handleSeekStart,
    handleSeekChange,
    handleSeekEnd,
    handleVolumeChange,
    toggleMute,
    toggleFullscreen,
    handleNativePlay,
    handleNativePause,
    handleEnded,
    isBuffering,
    handleWaiting,
    handlePlaying,
    handleCanPlay,
  } = useVideoPlayer(playbackUrl, playerMeta);

  useEffect(() => {
    if (src) {
      setPlaybackUrl(src);
      return;
    }

    if (!videoId) {
      setPlaybackUrl(null);
      return;
    }

    let isCancelled = false;

    const getTicket = async () => {
      try {
        const ticket = await videoPlaybackTicket.mutateAsync(videoId);
        if (!isCancelled) {
          setPlaybackUrl(ticket.playbackUrl);
        }
      } catch (error) {
        if (!isCancelled) {
          setPlaybackUrl(null);
          try {
            if (error instanceof Error) {
              onErrorShowAllErrorsToast(error);
            }
          } catch {
            // Non-API errors are not JSON payloads.
          }
        }
      }
    };

    void getTicket();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when source changes
  }, [videoId, src]);

  const mediaStyle: CSSProperties = {
    aspectRatio: `${aspectRatio} / 1`,
  };

  const seekMax = duration || 100;
  const volumePercent = volume * 100;
  const isLoading = videoPlaybackTicket.isPending || isBuffering;

  return (
    <div className='video-player'>
      <div className='video-player-media' style={mediaStyle}>
        <video
          ref={videoRef}
          src={playbackUrl || undefined}
          controls={false}
          preload='metadata'
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onPlay={handleNativePlay}
          onPause={handleNativePause}
          onEnded={handleEnded}
          onWaiting={handleWaiting}
          onPlaying={handlePlaying}
          onCanPlay={handleCanPlay}
          onClick={togglePlay}
        />
      </div>

      <div className='video-player-controls'>
        <Button
          type='button'
          className='video-player-play'
          variant={BUTTON_VARIANT.submit}
          onlyIcon
          isLoading={isLoading}
          disabled={!playbackUrl && !isLoading}
          onClick={togglePlay}>
          <Button.Description
            description={isPlaying ? t('button.pause') : t('button.play')}
          />
          <Button.Icon icon={isPlaying ? <PiPauseFill /> : <PiPlayFill />} />
        </Button>

        <div className='video-player-slider'>
          <p className='video-player-slider-time'>
            {formatPlaybackTime(currentTime)}
          </p>

          <Input.Slider
            className='video-player-seek'
            min={0}
            max={seekMax}
            step='any'
            value={currentTime}
            onMouseDown={handleSeekStart}
            onTouchStart={handleSeekStart}
            onChange={event => handleSeekChange(Number(event.target.value))}
            onMouseUp={handleSeekEnd}
            onTouchEnd={handleSeekEnd}
          />

          <p className='video-player-slider-time'>
            {formatPlaybackTime(duration)}
          </p>
        </div>

        <div className='video-player-sound'>
          <Button type='button' isClear onlyIcon onClick={toggleMute}>
            <Button.Description description={t('button.toggle-sound')} />
            <Button.Icon
              icon={volume === 0 ? <PiSpeakerX /> : <PiSpeakerHigh />}
            />
          </Button>

          <Input.Slider
            className='video-player-volume'
            min={0}
            max={100}
            step={5}
            value={volumePercent}
            onChange={event =>
              handleVolumeChange(Number(event.target.value) / 100)
            }
          />
        </div>

        <Button type='button' isClear onlyIcon onClick={toggleFullscreen}>
          <Button.Description description={t('button.fullscreen')} />
          <Button.Icon icon={<PiCornersOut />} />
        </Button>
      </div>
    </div>
  );
};

export default VideoPlayer;
